import React, { useState } from 'react';
import Button from '../../../components/base/Button';

export default function BooksSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showChapterPreview, setShowChapterPreview] = useState(false);

  const books = [
    {
      id: 1,
      title: 'Nurturing the Nature',
      author: 'Dr. Ajay Girotra',
      description: 'A practical handbook on oxygen deficiency, its impact on human health, and the benefits of supplementation for everyday life.',
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/717ff7e4e4d164bc84a31d8bf3d11780.jfif',
      category: 'Medical Guide',
      features: ['Easy-to-understand explanations', 'Covers major organs and illnesses', 'Real-life examples and applications', 'Simplified science for daily wellness'],
      status: 'Available on Amazon',
      chapterPreview: {
        title: 'Nurturing the Nature – Chapter Preview',
        sections: [
          {
            title: 'Preface',
            description: 'An introduction to the silent crisis of oxygen deficiency and why awareness matters today.'
          },
          {
            title: 'Compendium',
            description: 'A comprehensive overview of oxygen—its role in the human body, environment, and global health'
          },
          {
            title: 'Chapter 1 – What is Oxygen?',
            description: 'Exploring the science of oxygen, its presence in nature, and its role in sustaining life'
          },
          {
            title: 'Chapter 2 – How Oxygen Deficiency Affects the Body',
            description: 'Detailed insights into the impact of low oxygen levels on the brain, heart, lungs, kidneys, liver, muscles, and stomach'
          },
          {
            title: 'Chapter 3 – How Oxygen Helps in Illness and Everyday Life',
            description: 'Practical applications of oxygen therapy and supplementation across conditions and scenarios:',
            subPoints: [
              'Diabetes & Hypertension',
              'Cancer',
              'Skin Diseases, Wounds & Burns',
              'Trauma, Cold & Cough',
              'Women\'s Health (Menstrual Periods, PCOD/PCOS)',
              'Pollution & Sleep Apnea',
              'Eye & Dental Health',
              'Exercise, Sports & High-Altitude living',
              'Armed Forces & Police resilience',
              'Agriculture, Fossil Fuels & Animal health'
            ]
          },
          {
            title: 'Chapter 4 – Existing Oxygen Supplementing Methods',
            description: 'A survey of traditional and modern approaches—from cylinders and concentrators to emerging solid-state oxygen solutions'
          }
        ]
      }
    },
    {
      id: 2,
      title: 'Pocket Guide to Oxygen Health',
      author: 'Medical Team',
      description: 'A concise, portable reference guide designed for healthcare professionals and community workers. Contains essential information for quick consultation and emergency situations.',
      features: ['Compact 150-page format', 'Quick reference charts', 'Emergency protocols', 'Waterproof and durable design'],
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/f2b6bb58e84c10cfc2d8455056bb785c.png',
      category: 'Reference Guide',
      status: 'Coming Soon'
    },
    {
      id: 3,
      title: 'Community Health Manual',
      author: 'Community Health Team',
      description: 'Specially designed for rural healthcare workers and community leaders. Focuses on practical, low-cost interventions and early detection methods.',
      features: ['Simple, illustrated instructions', 'Low-cost intervention methods', 'Community workshop templates', 'Multi-language support'],
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/ee0e75a550f8bedd4a117edc57339673.jfif',
      category: 'Community Guide',
      status: 'In Development'
    },
    {
      id: 4,
      title: "Children's Oxygen Adventure",
      author: 'Educational Team',
      description: 'An engaging, colorful book that teaches children about the importance of breathing and oxygen health through fun stories and interactive activities.',
      features: ['Age-appropriate content (5-12 years)', 'Interactive activities and games', 'Colorful illustrations', 'Parent guidance section'],
      image: 'https://static.readdy.ai/image/9842b5143901a0019f067b22648287f2/2821500ebe0f723dc04633856a8e6d5d.png',
      category: "Children's Book",
      status: 'Coming Soon'
    }
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % books.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + books.length) % books.length);
  };

  return (
    <section className="py-20 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
            Educational Books &amp; Publications
          </h2>
          <p className="text-xl text-gray-700 leading-relaxed">
            Comprehensive guides and references that make oxygen health knowledge 
            accessible to everyone - from healthcare professionals to community members.
          </p>
        </div>

        {/* Books Carousel */}
        <div className="relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Book Display */}
            <div className="relative">
              <img 
                src={books[currentSlide].image} 
                alt={books[currentSlide].title} 
                className="w-full max-w-lg mx-auto h-[600px] object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute top-4 right-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  books[currentSlide].status === 'Available Now' 
                    ? 'bg-green-100 text-green-800' 
                    : books[currentSlide].status === 'Available on Amazon'
                    ? 'bg-purple-100 text-purple-800'
                    : books[currentSlide].status === 'Coming Soon'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-orange-100 text-orange-800'
                }`}>
                  {books[currentSlide].status}
                </span>
              </div>
            </div>

            {/* Book Details */}
            <div className="space-y-6">
              <div>
                <span className="text-sm text-[#12B6B6] font-medium uppercase tracking-wider">
                  {books[currentSlide].category}
                </span>
                <h3 className="text-3xl md:text-4xl font-bold text-[#111111] mb-2">
                  {books[currentSlide].title}
                </h3>
                <p className="text-lg text-gray-600 font-medium">
                  by {books[currentSlide].author}
                </p>
              </div>

              <p className="text-gray-700 text-lg leading-relaxed">
                {books[currentSlide].description}
              </p>

              {/* Features */}
              <div className="space-y-3">
                <h4 className="font-semibold text-[#111111]">Key Features:</h4>
                <ul className="space-y-2">
                  {books[currentSlide].features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <i className="ri-check-line w-5 h-5 flex items-center justify-center text-[#12B6B6] mt-0.5"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex space-x-4">
                <Button variant="primary" size="lg">
                  {books[currentSlide].status === 'Available on Amazon' ? 'Buy on Amazon.in' : books[currentSlide].status === 'Available Now' ? 'Download Free' : 'Get Notified'}
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => setShowChapterPreview(true)}
                >
                  Preview Chapters
                </Button>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button 
            onClick={prevSlide}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#12B6B6] hover:text-white transition-all duration-200 z-10"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          <button 
            onClick={nextSlide}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#12B6B6] hover:text-white transition-all duration-200 z-10"
          >
            <i className="ri-arrow-right-line text-xl"></i>
          </button>

          {/* Slide Indicators */}
          <div className="flex justify-center space-x-2 mt-8">
            {books.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-3 h-3 rounded-full transition-colors duration-200 ${
                  index === currentSlide ? 'bg-[#12B6B6]' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Chapter Preview Modal */}
        {showChapterPreview && books[currentSlide].chapterPreview && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 p-6 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <h3 className="text-2xl font-bold text-[#111111]">
                    {books[currentSlide].chapterPreview.title}
                  </h3>
                  <button
                    onClick={() => setShowChapterPreview(false)}
                    className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                  >
                    <i className="ri-close-line text-xl text-gray-600"></i>
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                {books[currentSlide].chapterPreview.sections.map((section, index) => (
                  <div key={index} className="border-l-4 border-[#12B6B6] pl-6">
                    <h4 className="text-xl font-bold text-[#111111] mb-3">
                      {section.title}
                    </h4>
                    <p className="text-gray-700 leading-relaxed mb-3">
                      {section.description}
                    </p>
                    {section.subPoints && (
                      <ul className="space-y-2 ml-4">
                        {section.subPoints.map((point, pointIndex) => (
                          <li key={pointIndex} className="flex items-start space-x-3">
                            <span className="w-2 h-2 bg-[#12B6B6] rounded-full mt-2 flex-shrink-0"></span>
                            <span className="text-gray-600">{point}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
                
                <div className="mt-8 p-6 bg-[#FAFAFA] rounded-xl text-center">
                  <p className="text-gray-600 mb-4">
                    Ready to dive deeper into oxygen health knowledge?
                  </p>
                  <Button variant="primary" size="lg">
                    Buy on Amazon.in
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
